// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './ContaminantsSection.scss';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import BaleCard from '../BaleCard/BaleCard';
import {
  getAllLabelsetsBelongingToGroup,
  getLabelDisplayName,
} from '../../shared_components/utils/FirebaseLabelsetsUtils';
import { getBaleIdsByContaminant } from '../../shared_components/utils/DeliveryAnalyticsUtils';
import { TriangleAlert } from 'lucide-react';

const DETECTION_CONF_THRESHOLD = 0.5;

export default function ContaminantsSection({ delivery, deliveryId, importedBalesData, showBale, setAnalysisSummary }) {
  const { t, i18n } = useTranslation();
  const globalLabelsets = useAuthStore((state) => state.globalLabelsets);
  const globalLabelsetGroups = useAuthStore((state) => state.globalLabelsetGroups);
  const [expanded, setExpanded] = React.useState(false);

  const contaminationStats = React.useMemo(() => {
    const contaminantsLabelsetIds = getAllLabelsetsBelongingToGroup(globalLabelsets, 'contaminants');
    const detections = Object.entries(delivery?.analytics?.data_per_labelset || {}).reduce(
      (acc, [labelsetId, labelsetData]) => {
        if (contaminantsLabelsetIds.includes(labelsetId)) {
          acc = { ...acc, ...labelsetData?.detection_counts };
        }
        return acc;
      },
      {},
    );
    const baleIdsByContaminant = getBaleIdsByContaminant(importedBalesData, globalLabelsets, DETECTION_CONF_THRESHOLD);
    return {
      totalContaminants: Object.values(detections).reduce((sum, value) => sum + value, 0),
      uniqueContaminantTypes: Object.keys(detections).length,
      hasContamination: Object.keys(baleIdsByContaminant).length > 0,
      baleIdsByContaminant: baleIdsByContaminant,
    };
  }, [deliveryId, importedBalesData, delivery?.analytics?.data_per_labelset, globalLabelsets]);

  React.useEffect(() => {
    setAnalysisSummary((prev) => ({
      ...prev,
      numContaminations: contaminationStats.totalContaminants,
    }));
  }, [contaminationStats.totalContaminants]);

  return (
    <div className='delivery--ai-nested-card expandable'>
      <div className='delivery--ai-nested-card-header' onClick={() => setExpanded(!expanded)}>
        <div className='delivery--ai-nested-card-info'>
          <div className='delivery--ai-nested-card-icon-wrapper red'>
            <TriangleAlert className='delivery--ai-nested-card-icon' />
          </div>
          <div className='delivery--ai-nested-card-text'>
            <h4 className='delivery--ai-nested-card-title'>
              {t('contaminants') || 'Contaminants'}
              <span className='beta-badge'>BETA</span>
            </h4>
            <p className='delivery--ai-nested-card-subtitle'>
              {!contaminationStats.hasContamination
                ? t('no_contamination') || 'No contamination detected'
                : contaminationStats.uniqueContaminantTypes === 1
                  ? `${contaminationStats.uniqueContaminantTypes} ${t('type_detected') || 'type detected'}`
                  : `${contaminationStats.uniqueContaminantTypes} ${t('types_detected') || 'types detected'}`}
            </p>
          </div>
        </div>
        <div className='delivery--ai-nested-card-value'>
          <div className='delivery--ai-nested-card-main-value'>{contaminationStats.totalContaminants || '0'}</div>
        </div>
      </div>

      {expanded && (
        <div className='delivery--ai-nested-card-expanded'>
          <div className='delivery--ai-expanded-content'>
            {contaminationStats.hasContamination ? (
              <>
                {Object.entries(contaminationStats.baleIdsByContaminant).map(([contaminantType, baleIds]) => (
                  <div key={contaminantType} className='delivery--ai-contamination-row'>
                    <div className='delivery--ai-contamination-info'>
                      <span className='delivery--ai-breakdown-item'>
                        {getLabelDisplayName(globalLabelsetGroups?.contaminants, contaminantType, i18n?.language) ??
                          contaminantType}
                        :
                      </span>
                      <span className='delivery--ai-breakdown-count'>{baleIds.length}</span>
                    </div>
                    <div className='delivery--ai-contamination-images'>
                      {(() => {
                        const displayedBaleIds = baleIds.slice(0, 8);
                        return (
                          <>
                            {displayedBaleIds.map((baleId) => {
                              const bale = delivery.bales?.[baleId];
                              if (!bale || !bale.image_storage_path) return null;
                              return (
                                <div
                                  key={baleId}
                                  className='delivery--ai-contamination-bale-image'
                                  onClick={() => showBale(baleId)}
                                >
                                  <BaleCard
                                    imageStoragePath={bale.image_storage_path}
                                    flagged={false}
                                    baleId={baleId}
                                    deliveryId={deliveryId}
                                  />
                                </div>
                              );
                            })}
                            {baleIds.length > 8 && (
                              <div className='delivery--ai-contamination-see-more'>
                                {t('see_more') || 'See more'}
                                <span className='delivery--ai-see-more-count'>+{baleIds.length - 8}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className='delivery--ai-error-images-grid'>
                <div className='delivery--ai-no-errors'>
                  <div className='delivery--ai-no-errors-icon'>✓</div>
                  <p>
                    {t('no_contamination_detected_message') ||
                      'No contamination detected in bales with good image quality'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
